"use client";

import React, { createContext, useState, useContext, useMemo } from "react";

import { MarketingConsent } from "@/types/MarketingConsent";
import { PaymentDetails } from "@/types/payment-details-types";

interface AppContextType {
  marketingConsent: MarketingConsent;
  paymentDetails?: PaymentDetails | null;
  setMarketingConsent: (marketingConsent: MarketingConsent) => void;
  setPaymentDetails: (paymentDetails: PaymentDetails) => void;
  optOutAutoRenewal: boolean;
  setOptOutAutoRenewal: (optOutAutoRenewal: boolean) => void;
}

const RenewalReviewStateContext = createContext<AppContextType>({
  marketingConsent: {
    email: false,
    sms: false,
    telephone: false,
    post: false,
  },
  paymentDetails: null,
  optOutAutoRenewal: false,
  setMarketingConsent: () => {},
  setPaymentDetails: () => {},
  setOptOutAutoRenewal: () => {},
});

type RenewalStateProviderProps = {
  children: React.ReactNode;
};

function RenewalReviewStateProvider({
  children,
}: Readonly<RenewalStateProviderProps>) {
  const [marketingConsent, setMarketingConsent] = useState<MarketingConsent>({
    email: false,
    sms: false,
    telephone: false,
    post: false,
  });
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(
    null
  );
  const [optOutAutoRenewal, setOptOutAutoRenewal] = useState<boolean>(false);

  const value = useMemo(
    () => ({
      marketingConsent,
      paymentDetails,
      optOutAutoRenewal,
      setMarketingConsent,
      setPaymentDetails,
      setOptOutAutoRenewal,
    }),
    [marketingConsent, paymentDetails, optOutAutoRenewal]
  );

  return (
    <RenewalReviewStateContext.Provider value={value}>
      {children}
    </RenewalReviewStateContext.Provider>
  );
}

export const useRenewalReviewStateContext = () =>
  useContext(RenewalReviewStateContext);

export default RenewalReviewStateProvider;
